import React, { useState } from 'react';

interface AIReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (config: ReportConfig) => void;
  isGenerating: boolean;
}

export interface ReportConfig {
  reportPurpose: string;
  audienceType: 'internal' | 'external';
}

const AIReportModal: React.FC<AIReportModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  isGenerating
}) => {
  const [reportPurpose, setReportPurpose] = useState('');
  const [audienceType, setAudienceType] = useState<'internal' | 'external'>('external');

  if (!isOpen) return null;

  const handleGenerate = () => {
    onGenerate({ reportPurpose, audienceType });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Generate AI Report</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isGenerating}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Report Purpose */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Context
              <span className="text-gray-500 font-normal ml-2">(optional)</span>
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              placeholder="e.g., 'Report for client XYZ' or 'Focus on contract vs permanent split'"
              value={reportPurpose}
              onChange={(e) => setReportPurpose(e.target.value)}
              disabled={isGenerating}
            />
            <p className="text-xs text-gray-500 mt-1">
              Data is already filtered. Add any extra context to customize the report's narrative.
            </p>
          </div>

          {/* Audience Type */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Report Audience
            </label>
            <div className="flex gap-4">
              <button
                className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                  audienceType === 'external'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setAudienceType('external')}
                disabled={isGenerating}
              >
                <div className="font-semibold mb-1">External (Client)</div>
                <div className="text-xs opacity-75">Professional, client-facing language</div>
              </button>
              <button
                className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                  audienceType === 'internal'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setAudienceType('internal')}
                disabled={isGenerating}
              >
                <div className="font-semibold mb-1">Internal (Team)</div>
                <div className="text-xs opacity-75">Strategic insights, BD opportunities</div>
              </button>
            </div>
          </div>

          {/* Info Box */}
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-blue-800">
                <div className="font-semibold mb-1">AI-Powered Professional Reports</div>
                <div>Claude AI will analyze your data and generate:</div>
                <ul className="list-disc ml-4 mt-2 space-y-1">
                  <li>Executive summary with market insights</li>
                  <li>5-7 key findings with strategic context</li>
                  <li>3-4 actionable opportunities</li>
                  <li>Supporting charts and data visualization</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              disabled={isGenerating}
            >
              Cancel
            </button>
            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isGenerating ? (
                <>
                  <span className="animate-spin">⏳</span>
                  <span>Generating Report...</span>
                </>
              ) : (
                <>
                  <span>✨</span>
                  <span>Generate AI Report</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIReportModal;
